// src/Pages/Dashboard.jsx
import React from 'react';
import { useGetDashboardSummaryQuery } from '../redux/services/dashboardServices/dashboard.Services';
import {
  MdGroup,
  MdPeople,
  MdHome,
  MdLocationCity,
  MdPublic,
  MdMale,
  MdFemale,
  MdEvent,
  MdAdminPanelSettings,
  MdPending,
  MdCheckCircle,
  MdHouse,
  MdPerson,
  MdPersonAdd,
  MdCheck,
  MdWarning
} from 'react-icons/md';
import { FaSpinner } from 'react-icons/fa';

// 🎨 Color Palette (Same as your screenshot)
const COLORS = {
  totalPariwar: '#4f46e5', // Indigo
  active: '#10b981',       // Green
  pending: '#f59e0b',      // Amber
  totalSadasya: '#0ea5e9', // Sky Blue
  waad: '#8b5cf6',         // Purple
  gaon: '#10b981',         // Emerald
  male: '#3b82f6',         // Blue
  female: '#ec4899',       // Pink
  event: '#f59e0b',        // Amber
  subadmin: '#ef4444'      // Red
};

const Dashboard = () => {
  const { data, isLoading } = useGetDashboardSummaryQuery();

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="text-center">
          <FaSpinner className="spinner-border text-primary" size={40} />
          <h5 className="mt-3">Loading Dashboard Data...</h5>
        </div>
      </div>
    );
  }

  const stats = data?.data || {};

  // ================= CARDS DATA (English) =================
  const tiles = [
    // ---- PARIWAR ----
    {
      icon: MdHome,
      label: 'Total Families',
      value: stats.pariwar?.total || 0,
      bg: 'linear-gradient(135deg, #eef2ff, #c7d2fe)',
      borderColor: COLORS.totalPariwar,
      iconBg: '#4f46e5'
    },
    {
      icon: MdCheckCircle,
      label: 'Active Families',
      value: stats.pariwar?.active || 0,
      bg: 'linear-gradient(135deg, #ecfdf5, #a7f3d0)',
      borderColor: COLORS.active,
      iconBg: '#10b981'
    },
    {
      icon: MdPending,
      label: 'Pending Families',
      value: stats.pariwar?.pending || 0,
      bg: 'linear-gradient(135deg, #fef3c7, #fcd34d)',
      borderColor: COLORS.pending,
      iconBg: '#f59e0b'
    },

    // ---- SADASYA ----
    {
      icon: MdPeople,
      label: 'Total Members',
      value: stats.sadasya?.total || 0,
      bg: 'linear-gradient(135deg, #eff6ff, #bfdbfe)',
      borderColor: COLORS.totalSadasya,
      iconBg: '#0ea5e9'
    },
    {
      icon: MdCheckCircle,
      label: 'Active Members',
      value: stats.sadasya?.active || 0,
      bg: 'linear-gradient(135deg, #ecfdf5, #a7f3d0)',
      borderColor: COLORS.active,
      iconBg: '#10b981'
    },
    {
      icon: MdPending,
      label: 'Pending Members',
      value: stats.sadasya?.pending || 0,
      bg: 'linear-gradient(135deg, #fef3c7, #fcd34d)',
      borderColor: COLORS.pending,
      iconBg: '#f59e0b'
    },

    // ---- LOCATION ----
    {
      icon: MdLocationCity,
      label: 'Total Waads',
      value: stats.waad?.total || 0,
      bg: 'linear-gradient(135deg, #f3e8ff, #d8b4fe)',
      borderColor: COLORS.waad,
      iconBg: '#8b5cf6'
    },
    {
      icon: MdPublic,
      label: 'Total Gaons',
      value: stats.gaon?.total || 0,
      bg: 'linear-gradient(135deg, #d1fae5, #6ee7b7)',
      borderColor: COLORS.gaon,
      iconBg: '#10b981'
    },

    // ---- GENDER ----
    {
      icon: MdMale,
      label: 'Total Males',
      value: stats.gender?.male || 0,
      bg: 'linear-gradient(135deg, #dbeafe, #93c5fd)',
      borderColor: COLORS.male,
      iconBg: '#3b82f6'
    },
    {
      icon: MdFemale,
      label: 'Total Females',
      value: stats.gender?.female || 0,
      bg: 'linear-gradient(135deg, #fce7f3, #f9a8d4)',
      borderColor: COLORS.female,
      iconBg: '#ec4899'
    },

    // ---- EVENTS & SUB ADMIN ----
    {
      icon: MdEvent,
      label: 'Total Events',
      value: stats.event?.total || 0,
      bg: 'linear-gradient(135deg, #fef3c7, #fcd34d)',
      borderColor: COLORS.event,
      iconBg: '#f59e0b'
    },
    {
      icon: MdAdminPanelSettings,
      label: 'Total Sub-Admins',
      value: stats.sub_admin?.total || 0,
      bg: 'linear-gradient(135deg, #fee2e2, #fca5a5)',
      borderColor: COLORS.subadmin,
      iconBg: '#ef4444'
    },
  ];

  return (
    <div className="dashboard">
      {/* HEADER (English) */}
      <div className="dashboard-header">
        <div className="dashboard-header-inner">

          <div className="header-content">


            <h2 className="page-title">
              Admin Dashboard
            </h2>

            <div className="custom-breadcrumb">
              <span>Home</span>
              <span>/</span>
              <span className="active">Dashboard</span>
            </div>
          </div>



        </div>
      </div>
      <div className="container-fluid">

        {/* STATS CARDS */}
        <div className="row g-4">
          {tiles.map((item, index) => {
            const IconComponent = item.icon;

            return (
              <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={index}>
                <div className="card border-0 overflow-hidden shadow-sm h-100"
                  style={{
                    background: item.bg,
                    borderLeft: `6px solid ${item.borderColor}`,
                    borderRadius: '12px',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: 'default'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 0.125rem 0.25rem rgba(0,0,0,0.075)';
                  }}
                >
                  <div className="card-body d-flex align-items-center justify-content-between p-3">
                    {/* Left: Icon */}
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: 'rgba(255,255,255,0.5)',
                        color: item.iconBg
                      }}
                    >
                      <IconComponent size={26} />
                    </div>

                    {/* Right: Value & Label */}
                    <div className="text-end flex-grow-1">
                      <h3 className="mb-0 fw-bold" style={{  color: '#111827' }}>
                        {item.value?.toLocaleString('en-US') || 0}
                      </h3>
                      <p className="labeldashboard">
                        {item.label}
                      </p>
                    </div>
                  </div>
                  <div className="linkview">
                    <a href="#" className="buttonview">View Details</a>
                  </div>
                </div>

              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default Dashboard;